import { storeToRefs } from "pinia";
import { computed } from "vue";

import { getLocale, getLocaleNumber } from "shared/boot/i18n";
import type { Currency } from "shared/constants";
import { currencies } from "shared/constants";
import { useUserStore } from "shared/stores/user";

interface NumberToCurrency {
  codeOnFront: boolean;
  currencyCode: string;
  currencyDisplay: string;
  locale: string | undefined;
}

interface NumberToCurrentUserCurrency {
  codeOnFront?: boolean;
  currencyDisplay?: string;
}

export default function useCurrency() {
  const currencyCodeReplacements: RegExp = /ISK|ZAR|BTC/;
  const userStore = useUserStore();
  const { currentUser } = storeToRefs(userStore);

  function currencyFromCode(currencyCode: string): Currency | undefined {
    return currencies.find((currency) => currency.code === currencyCode);
  }

  function currencyFromLocale(locale: string): Currency | undefined {
    return currencies.find((currency) => currency.locale === locale);
  }

  function numberToCurrency(
    number: number,
    { codeOnFront, currencyCode, currencyDisplay, locale }: NumberToCurrency
  ): string {
    const currency = getLocaleNumber(
      number,
      {
        currency: currencyCode,
        currencyDisplay,
        minimumFractionDigits: 0,
        style: "currency",
      },
      (locale ? `${locale}-u-nu-latn` : null) as any
    );

    if (currencyDisplay === "symbol") {
      const currencyCodeMatches = currency.match(currencyCodeReplacements);

      if (currencyCodeMatches) {
        const symbol = currencyFromCode(currencyCodeMatches[0])?.symbolNative;

        if (symbol) return currency.replace(currencyCodeMatches[0], symbol);
      }
    } else if (currencyDisplay === "code") {
      if (codeOnFront) {
        const code = currency.match(/[A-Z]+/);

        if (code) {
          return `${code[0]} ${currency.replace(code[0], "").trim()}`;
        }
      }
    }

    return currency;
  }

  const currentUserCurrency = computed<Currency | undefined>(
    () =>
      currencyFromCode(currentUser.value.currency_code ?? "") ||
      currencyFromLocale(getLocale())
  );

  function numberToCurrentUserCurrency(
    number: number,
    {
      codeOnFront = false,
      currencyDisplay = "symbol",
    }: NumberToCurrentUserCurrency = {}
  ): string {
    return currentUserCurrency.value
      ? numberToCurrency(number, {
          codeOnFront,
          currencyCode: currentUserCurrency.value.code,
          currencyDisplay,
          locale: currentUserCurrency.value.locale,
        })
      : numberToCurrency(number, {
          codeOnFront,
          currencyCode: "AUD",
          currencyDisplay,
          locale: "en",
        });
  }

  return {
    currentUserCurrency,
    numberToCurrentUserCurrency,
  };
}
